// Service Worker Registration and Management
// Handles registration, updates, and offline capabilities

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

export function register(config) {
  if ('serviceWorker' in navigator) {
    // Check if we should register the service worker
    if (process.env.NODE_ENV === 'production' || isLocalhost) {
      // The URL constructor is available in all browsers that support SW.
      const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
      if (publicUrl.origin !== window.location.origin) {
        // Our service worker won't work if PUBLIC_URL is on a different origin
        return;
      }

      window.addEventListener('load', () => {
        const swUrl = `${process.env.PUBLIC_URL}/sw.js`;

        if (isLocalhost) {
          // This is running on localhost. Check if a service worker still exists or not.
          checkValidServiceWorker(swUrl, config);

          // Add some additional logging to localhost, pointing developers to the
          // service worker/PWA documentation.
          navigator.serviceWorker.ready.then(() => {
            console.log(
              '🎉 This web app is being served cache-first by a service worker. ' +
              'To learn more, visit https://cra.link/PWA'
            );
          });
        } else {
          // Is not localhost. Just register service worker
          registerValidSW(swUrl, config);
        }
      });
    }
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('✅ Service Worker registered successfully:', registration);
      
      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        
        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated content has been fetched from the network
              console.log('🔄 New content is available and will be used when all tabs are closed.');
              
              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
              
              // Notify user about update
              notifyUserAboutUpdate(registration);
            } else {
              // At this point, everything has been cached for offline use
              console.log('📱 Content is cached for offline use.');
              
              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        });
      });
      
      // Check for updates every 24 hours
      setInterval(() => {
        registration.update();
      }, 24 * 60 * 60 * 1000);
      
    })
    .catch((error) => {
      console.error('❌ Service Worker registration failed:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        '📱 No internet connection found. App is running in offline mode.'
      );
    });
}

function notifyUserAboutUpdate(registration) {
  // Create a simple notification about the update
  const updateAvailable = window.confirm(
    'A new version of the app is available. Would you like to update now?'
  );
  
  if (updateAvailable && registration.waiting) {
    // Tell the waiting service worker to skip waiting and become active
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Refresh the page to get the new version
    window.location.reload();
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log('🗑️ Service Worker unregistered');
      })
      .catch((error) => {
        console.error('❌ Service Worker unregistration failed:', error.message);
      });
  }
}

// PWA Install Prompt
export function setupPWAInstallPrompt() {
  let deferredPrompt;
  
  window.addEventListener('beforeinstallprompt', (e) => {
    console.log('💾 PWA install prompt available');
    // Prevent Chrome 67 and earlier from automatically showing the prompt
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    
    // Show custom install button/banner
    showInstallButton(deferredPrompt);
  });
  
  // Handle PWA installation
  window.addEventListener('appinstalled', (e) => {
    console.log('🎉 PWA was installed successfully');
    hideInstallButton();
  });
}

function showInstallButton(deferredPrompt) {
  // Create or show install button
  let installButton = document.getElementById('pwa-install-button');
  
  if (!installButton) {
    installButton = document.createElement('button');
    installButton.id = 'pwa-install-button';
    installButton.innerHTML = '📱 Install App';
    installButton.className = 'fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-blue-700 transition-colors z-50';
    installButton.style.display = 'block';
    document.body.appendChild(installButton);
  }
  
  installButton.addEventListener('click', async () => {
    // Hide the button
    installButton.style.display = 'none';
    
    // Show the prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`👤 User response to install prompt: ${outcome}`);
    
    // Clear the deferredPrompt variable
    deferredPrompt = null;
  });
}

function hideInstallButton() {
  const installButton = document.getElementById('pwa-install-button');
  if (installButton) {
    installButton.style.display = 'none';
  }
}

// Cache Management Functions
export const cacheManager = {
  // Clear all caches
  clearAllCaches: async () => {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('🗑️ All caches cleared');
    }
  },
  
  // Get cache statistics
  getCacheStats: async () => {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      const stats = {};
      
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        stats[cacheName] = {
          name: cacheName,
          itemCount: keys.length,
          urls: keys.map(req => req.url)
        };
      }
      
      return stats;
    }
    return {};
  },
  
  // Force cache update
  updateCaches: async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'UPDATE_CACHES' });
      console.log('🔄 Cache update requested');
    }
  }
};

// Offline status management
export function setupOfflineHandling() {
  // Show offline indicator
  function showOfflineIndicator() {
    let offlineIndicator = document.getElementById('offline-indicator');
    
    if (!offlineIndicator) {
      offlineIndicator = document.createElement('div');
      offlineIndicator.id = 'offline-indicator';
      offlineIndicator.innerHTML = '📱 You are offline - some features may be limited';
      offlineIndicator.className = 'fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-2 z-50';
      document.body.appendChild(offlineIndicator);
    }
    
    offlineIndicator.style.display = 'block';
  }
  
  function hideOfflineIndicator() {
    const offlineIndicator = document.getElementById('offline-indicator');
    if (offlineIndicator) {
      offlineIndicator.style.display = 'none';
    }
  }
  
  // Listen for online/offline events
  window.addEventListener('online', () => {
    console.log('🌐 Back online');
    hideOfflineIndicator();
  });
  
  window.addEventListener('offline', () => {
    console.log('📱 Gone offline');
    showOfflineIndicator();
  });
  
  // Initial check
  if (!navigator.onLine) {
    showOfflineIndicator();
  }
}